import { useState } from 'react';
import { Check, X, HelpCircle, ChevronDown, ChevronUp, Calculator, ListChecks, Sparkles } from 'lucide-react';
import { ExecutiveSummaryModal } from '@/components/ExecutiveSummary';
import AbbrTip from '@/components/AbbrTip';
import AbbrText from '@/components/AbbrText';
import CompareThesesButton from '@/components/CompareThesesButton';

/**
 * BuyChecklist — renders the server-computed Chowder Score + asset-class-
 * aware 5-point Buy Checklist (Round 95z, Jun 2026). Lives between the
 * "Why this score?" panel and the DiviDrip Opinion banner on the
 * Triangle tab (Stock Modal) and the standalone /triangle/:ticker page.
 *
 * Props:
 *   chowder    — { score, threshold, status, tier, note, yield_pct, cagr_pct }
 *   checklist  — { checks: [...], passing, total, verdict }
 *   compact    — boolean to render a tighter modal layout (smaller fonts).
 */
const STATUS_STYLES = {
  pass: {
    cls: 'border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-800/50 text-emerald-900 dark:text-emerald-100',
    icon: <Check className="w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />,
  },
  fail: {
    cls: 'border-rose-300/60 bg-rose-50/60 dark:bg-rose-950/30 dark:border-rose-800/50 text-rose-900 dark:text-rose-100',
    icon: <X className="w-3.5 h-3.5 shrink-0 text-rose-600 dark:text-rose-400" />,
  },
  unknown: {
    cls: 'border-border bg-muted/30 text-muted-foreground',
    icon: <HelpCircle className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />,
  },
};

const CHOWDER_BAND = {
  pass: 'text-emerald-700 dark:text-emerald-300',
  close: 'text-amber-700 dark:text-amber-300',
  fail: 'text-rose-700 dark:text-rose-300',
  unknown: 'text-muted-foreground',
};

export default function BuyChecklist({ chowder, checklist, compact = false, ticker, onOpenExecutiveSummary }) {
  const [open, setOpen] = useState(true);
  const [briefOpen, setBriefOpen] = useState(false);
  if (!checklist || !Array.isArray(checklist.checks) || checklist.checks.length === 0) {
    return null;
  }

  const passing = checklist.passing ?? 0;
  const total = checklist.total ?? checklist.checks.length;
  const verdictTone =
    passing === total ? 'text-emerald-700 dark:text-emerald-300'
    : passing === total - 1 ? 'text-emerald-700 dark:text-emerald-300'
    : passing >= 3 ? 'text-amber-700 dark:text-amber-300'
    : 'text-rose-700 dark:text-rose-300';

  return (
    <div className="rounded-lg border bg-card" data-testid="buy-checklist-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
        data-testid="buy-checklist-toggle"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          <ListChecks className="w-3.5 h-3.5 text-violet-500" />
          DiviDrip&apos;s Buy Checklist
          <span className={`text-[10px] font-bold normal-case tracking-normal ${verdictTone}`}>
            ({passing}/{total})
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2" data-testid="buy-checklist-body">
          {/* Chowder Score row — informational on top */}
          {chowder && chowder.score !== null && chowder.score !== undefined && chowder.tier !== 'etf' && (
            <div
              className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/30 px-2.5 py-1.5"
              data-testid="buy-checklist-chowder"
            >
              <Calculator className="w-3.5 h-3.5 shrink-0 mt-0.5 text-violet-500" />
              <div className="flex-1 text-[11px] font-bold leading-snug">
                <AbbrTip term="CHOWDER">Chowder Score</AbbrTip>
                <span>: </span>
                <span className={`font-mono font-bold ${CHOWDER_BAND[chowder.status] || ''}`}>
                  {chowder.score.toFixed(2)}
                </span>
                <span> (needs ≥{chowder.threshold.toFixed(0)})</span>
                <span className="block text-[10px] font-bold mt-0.5">{chowder.note}</span>
              </div>
            </div>
          )}

          {/* 5 (or up to 5) checks */}
          <ul className="space-y-1.5" data-testid="buy-checklist-checks">
            {checklist.checks.map((c, idx) => {
              const style = STATUS_STYLES[c.status] || STATUS_STYLES.unknown;
              return (
                <li
                  key={c.id || idx}
                  className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-[11px] font-bold leading-snug ${style.cls}`}
                  data-testid={`buy-checklist-check-${c.id}`}
                >
                  <span className="mt-0.5">{style.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-bold"><AbbrText text={c.label} /></span>
                      <span className="font-mono font-bold text-[11px]"><AbbrText text={c.value} /></span>
                      <span className="text-[10px] font-bold">target {c.target}</span>
                    </div>
                    <p className="text-[10px] font-bold mt-0.5"><AbbrText text={c.note} /></p>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Verdict footer */}
          <div
            className={`text-[11px] font-bold pt-1 border-t border-border/40 mt-1 ${verdictTone}`}
            data-testid="buy-checklist-verdict"
          >
            {passing}/{total} passed — {checklist.verdict}
          </div>
          <p className="text-[10px] font-bold text-foreground/80 leading-snug">
            <span className="font-bold">Rule of thumb:</span>{' '}
            5/5 = Conviction Buy · 4/5 = smaller position · 3/5 = investigate
            and write your thesis · ≤2/5 = walk away.
          </p>
          {ticker && (
            <div
              className="mt-2 pt-2 border-t border-border/40 flex items-center justify-between gap-3 flex-wrap"
              data-testid="buy-checklist-thesis-row"
            >
              <button
                type="button"
                onClick={() => {
                  if (onOpenExecutiveSummary) onOpenExecutiveSummary();
                  else setBriefOpen(true);
                }}
                className="flex items-center gap-2 text-left min-w-0 group"
                data-testid="buy-checklist-exec-summary-link"
                title="Open DiviDrip's Executive Summary — a Bull/Bear thesis starter for this ticker."
              >
                <Sparkles className="w-4 h-4 text-violet-500 shrink-0" />
                <span className="min-w-0">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    Write your Thesis
                  </span>
                  <span className="block text-sm font-bold truncate text-foreground group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">
                    {ticker} — Bull / Bear Thesis Starter
                  </span>
                </span>
              </button>
              <CompareThesesButton
                lockedTicker={ticker}
                label="Compare Theses"
                size="lg"
                testId="buy-checklist-compare-theses-btn"
              />
            </div>
          )}
        </div>
      )}

      {/* Round 95ah (Jun 2026) — Executive Summary popup. Only
          rendered when the parent didn't supply `onOpenExecutiveSummary`
          (i.e. on the standalone /triangle/:ticker page). The Stock
          Modal uses its own full-tab takeover instead — see
          components/StockModal.js. */}
      {!onOpenExecutiveSummary && ticker && (
        <ExecutiveSummaryModal
          ticker={ticker}
          open={briefOpen}
          onClose={() => setBriefOpen(false)}
        />
      )}
    </div>
  );
}
